import Color from 'color'

const getThemeFromUrlParams = (
    urlParams: CalculatorUrlParams
): CalculatorStyleProperties | null => {
    if (
        !urlParams.color ||
        !urlParams['background-color'] ||
        !(
            (urlParams['primary-button-background-color'] &&
                urlParams['primary-button-color'] &&
                urlParams['secondary-button-background-color'] &&
                urlParams['secondary-button-color']) ||
            urlParams['button-background-color'] ||
            urlParams['primary-button-color']
        )
    ) {
        return null
    }

    try {
        const color = new Color(urlParams.color),
            backgroundColor = new Color(urlParams['background-color']),
            primaryButtonBackgroundColor = new Color(
                urlParams['primary-button-background-color'] ??
                    urlParams['button-background-color']
            ),
            primaryButtonColor = new Color(
                urlParams['primary-button-color'] ?? urlParams['button-color']
            ),
            secondaryButtonBackgroundColor = new Color(
                urlParams['secondary-button-background-color'] ??
                    urlParams['button-color']
            ),
            secondaryButtonColor = new Color(
                urlParams['secondary-button-color'] ??
                    urlParams['button-background-color']
            )

        return {
            color: color.string(),
            backgroundColor: backgroundColor.string(),
            primaryButtonBackgroundColor: primaryButtonBackgroundColor.string(),
            primaryButtonColor: primaryButtonColor.string(),
            secondaryButtonBackgroundColor:
                secondaryButtonBackgroundColor.string(),
            secondaryButtonColor: secondaryButtonColor.string(),
            inputColor: '#131923',
            inputBackgroundColor: '#fff',
            inputBorderColor: 'rgb(242, 242, 242)',
            inputFocusBorderColor: primaryButtonBackgroundColor.string(),
            inputPlaceholderColor: '#dadada',
            labelColor: backgroundColor.isLight()
                ? 'rgba(0, 0, 0, 0.5)'
                : 'rgba(255, 255, 255, 0.5)',
            errorColor: 'red',
            spinnerColor: primaryButtonBackgroundColor.string(),
            logo: backgroundColor.isLight() ? 'orange-black' : 'white',
        }
    } catch {
        throw new Error(
            'The currently configured custom colors are not all valid CSS colors.'
        )
    }
}

export const getTheme = (
    urlParams: CalculatorUrlParams
): CalculatorStyleProperties | null => {
    const themeParam = urlParams.theme ?? 'light'

    switch (themeParam) {
        case 'dark':
            return {
                color: '#d8d8d8',
                backgroundColor: '#252525',
                primaryButtonBackgroundColor: '#ffa100',
                primaryButtonColor: '#fff',
                secondaryButtonBackgroundColor: '#fff',
                secondaryButtonColor: '#ffa100',
                inputColor: '#131923',
                inputBackgroundColor: '#fff',
                inputBorderColor: 'rgb(242, 242, 242)',
                inputPlaceholderColor: '#dadada',
                inputFocusBorderColor: 'rgb(5, 111, 255)',
                labelColor: 'rgba(255, 255, 255, 0.5)',
                errorColor: 'red',
                spinnerColor: '#ffa100',
                logo: 'white',
            }
        case 'custom':
            return getThemeFromUrlParams(urlParams)
        case 'light':
        default:
            return {
                color: '#131923',
                backgroundColor: '#fff',
                primaryButtonBackgroundColor: '#ffa100',
                primaryButtonColor: '#fff',
                secondaryButtonBackgroundColor: '#000',
                secondaryButtonColor: '#fff',
                inputColor: '#131923',
                inputBackgroundColor: '#fff',
                inputBorderColor: 'rgb(242, 242, 242)',
                inputPlaceholderColor: '#dadada',
                inputFocusBorderColor: 'rgb(5, 111, 255)',
                labelColor: 'rgba(0, 0, 0, 0.5)',
                errorColor: 'red',
                spinnerColor: '#ffa100',
                logo: 'orange-black',
            }
    }
}

export const applyTheme = (
    rootElement: HTMLElement,
    theme: CalculatorStyleProperties
) => {
    rootElement.style.backgroundColor = 'var(--background-color)'
    rootElement.style.color = 'var(--color)'

    rootElement.style.setProperty('--background-color', theme.backgroundColor)
    rootElement.style.setProperty('--color', theme.color)
    rootElement.style.setProperty(
        '--primary-button-background-color',
        theme.primaryButtonBackgroundColor
    )
    rootElement.style.setProperty(
        '--primary-button-color',
        theme.primaryButtonColor
    )
    rootElement.style.setProperty(
        '--secondary-button-background-color',
        theme.secondaryButtonBackgroundColor
    )
    rootElement.style.setProperty(
        '--secondary-button-color',
        theme.secondaryButtonColor
    )
    rootElement.style.setProperty('--input-color', theme.inputColor)
    rootElement.style.setProperty(
        '--input-background-color',
        theme.inputBackgroundColor
    )
    rootElement.style.setProperty(
        '--input-border-color',
        theme.inputBorderColor
    )
    rootElement.style.setProperty(
        '--input-focus-border-color',
        theme.inputFocusBorderColor
    )
    rootElement.style.setProperty(
        '--input-placeholder-color',
        theme.inputPlaceholderColor
    )
    rootElement.style.setProperty('--label-color', theme.labelColor)
    rootElement.style.setProperty('--error-color', theme.errorColor)
    rootElement.style.setProperty('--spinner-color', theme.spinnerColor)
}
